import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Alert as MuiAlert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Notifications,
  Security,
  Palette,
  Save,
  Refresh,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';
import TwoFactorSetup from '../components/auth/TwoFactorSetup';

interface UserSettings {
  notifications: {
    email: boolean;
    push: boolean;
    regulatory: boolean;
    alerts: boolean;
  };
  security: {
    twoFactor: boolean;
    sessionTimeout: number;
  };
  appearance: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    timezone: string;
  };
  data: {
    autoRefresh: boolean;
    refreshInterval: number;
  };
}

const SettingsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [settings, setSettings] = useState<UserSettings>({
    notifications: {
      email: true,
      push: true,
      regulatory: true,
      alerts: true,
    },
    security: {
      twoFactor: false,
      sessionTimeout: 30,
    },
    appearance: {
      theme: 'dark',
      language: 'en',
      timezone: 'UTC',
    },
    data: {
      autoRefresh: true,
      refreshInterval: 5,
    },
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load settings from backend on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try the new endpoint first
      try {
        const response = await api.get('/users/settings');
        setSettings(response.data);
        return;
      } catch (err: any) {
        LoggerService.debug('New settings endpoint not available, trying alternative...');
        
        // Fallback: try auth endpoint
        try {
          const response = await api.get('/auth/user-settings');
          setSettings(response.data);
          return;
        } catch (err2: any) {
          LoggerService.debug('Auth settings endpoint not available, using mock data...');
          
          // Final fallback: use mock data with a warning
          const mockSettings = {
            notifications: {
              email: true,
              push: true,
              regulatory: true,
              alerts: true,
            },
            security: {
              twoFactor: false,
              sessionTimeout: 60,
            },
            appearance: {
              theme: 'dark' as const,
              language: 'en',
              timezone: 'UTC',
            },
            data: {
              autoRefresh: true,
              refreshInterval: 30,
            },
            custom_settings: {},
          };
          
          setSettings(mockSettings);
          setError('Settings endpoint not available. Please restart the backend server to enable settings functionality.');
          return;
        }
      }
    } catch (err: any) {
      LoggerService.error('Error loading settings:', err);
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (key: keyof UserSettings['notifications']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: event.target.checked,
      },
    }));
  };

  const handleSecurityChange = (key: keyof UserSettings['security']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSettings(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [key]: event.target.checked,
      },
    }));
  };

  const handleAppearanceChange = (key: keyof UserSettings['appearance']) => (
    event: any
  ) => {
    setSettings(prev => ({
      ...prev,
      appearance: {
        ...prev.appearance,
        [key]: event.target.value,
      },
    }));
  };

  const handleDataChange = (key: keyof UserSettings['data']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSettings(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: event.target.type === 'checkbox' ? event.target.checked : parseInt(event.target.value),
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Call the backend API to save settings
      await api.put('/users/settings', settings);

      enqueueSnackbar('Settings saved successfully!', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      setError(null);

      // Call the backend API to reset settings
      const response = await api.post('/users/settings/reset');
      setSettings(response.data);

      enqueueSnackbar('Settings reset to defaults!', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Error resetting settings:', err);
      setError(err.response?.data?.detail || 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Page Header */}
          <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize your AmudFin experience
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <MuiAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </MuiAlert>
      )}

      <Grid container spacing={3}>
        {/* Notifications Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Notifications color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notifications
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.email}
                      onChange={handleNotificationChange('email')}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.push}
                      onChange={handleNotificationChange('push')}
                    />
                  }
                  label="Push Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.regulatory}
                      onChange={handleNotificationChange('regulatory')}
                    />
                  }
                  label="Regulatory Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.alerts}
                      onChange={handleNotificationChange('alerts')}
                    />
                  }
                  label="System Alerts"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Security color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Security
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.twoFactor}
                      onChange={handleSecurityChange('twoFactor')}
                    />
                  }
                  label="Two-Factor Authentication"
                />
                
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    security: {
                      ...prev.security,
                      sessionTimeout: parseInt(e.target.value) || 30,
                    },
                  }))}
                  inputProps={{ min: 5, max: 480 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Appearance Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Palette color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Appearance
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={settings.appearance.theme}
                    label="Theme"
                    onChange={handleAppearanceChange('theme')}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="auto">Auto</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={settings.appearance.language}
                    label="Language"
                    onChange={handleAppearanceChange('language')}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="fr">Français</MenuItem>
                    <MenuItem value="de">Deutsch</MenuItem>
                    <MenuItem value="es">Español</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.appearance.timezone}
                    label="Timezone"
                    onChange={handleAppearanceChange('timezone')}
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="CET">Central European Time</MenuItem>
                    <MenuItem value="EST">Eastern Time</MenuItem>
                    <MenuItem value="PST">Pacific Time</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Refresh color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Data & Performance
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.data.autoRefresh}
                      onChange={handleDataChange('autoRefresh')}
                    />
                  }
                  label="Auto-refresh Data"
                />
                
                <TextField
                  fullWidth
                  label="Refresh Interval (seconds)"
                  type="number"
                  value={settings.data.refreshInterval}
                  onChange={handleDataChange('refreshInterval')}
                  disabled={!settings.data.autoRefresh}
                  inputProps={{ min: 5, max: 300 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

            <Grid item xs={12}>
              <TwoFactorSetup />
            </Grid>

            {/* Action Buttons */}
      <Box mt={4} display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
        </>
      )}
    </Box>
  );
};

export default SettingsPage;
